import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import type { LabelPrinterDevice, LabelPrinterKind } from '@/lib/label/types'
import { LabelPrinterError } from '@/lib/label/types'
import type { LabelPrinterJob } from '@/context/label-printer'
import { cn } from '@/lib/utils'
import { showAware } from '@/lib/paths'
import { useShowSlug } from '@/hooks/useShowSlug'

type LabelPrinterPopoverProps = {
  strategy: LabelPrinterKind
  strategyLabel: string
  printers: LabelPrinterDevice[]
  selectedPrinter: LabelPrinterDevice | null
  loadingPrinters: boolean
  error: Error | null
  onRefresh: () => Promise<void>
  onSelectPrinter: (deviceId: string) => void
  onSetStrategy: (strategy: LabelPrinterKind) => void
  onTestPrint: () => Promise<void>
  jobs: LabelPrinterJob[]
}

function formatStatusLabel(status: LabelPrinterJob['status']) {
  switch (status) {
    case 'success':
      return 'Printed'
    case 'error':
      return 'Failed'
    default:
      return 'Pending'
  }
}

export function LabelPrinterPopoverControl(props: LabelPrinterPopoverProps) {
  const {
    strategy,
    strategyLabel,
    printers,
    selectedPrinter,
    loadingPrinters,
    error,
    onRefresh,
    onSelectPrinter,
    onSetStrategy,
    onTestPrint,
    jobs,
  } = props
  const [isTesting, setIsTesting] = useState(false)
  const currentSlug = useShowSlug()

  const recentJobs = jobs.slice(0, 4)
  const errorHelp =
    error instanceof LabelPrinterError && error.helpUrl
      ? {
          url: error.helpUrl,
          label: error.helpLabel ?? 'Open DYMO service in a new tab',
        }
      : null

  const handleTestPrint = async () => {
    if (isTesting) return
    setIsTesting(true)
    try {
      await onTestPrint()
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wide">Label Printer</span>
          <span className="text-sm font-semibold">{strategyLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Print strategy</p>
            <Badge variant="outline" className="text-xs uppercase tracking-wide">
              {strategyLabel}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={strategy === 'browser' ? 'default' : 'outline'}
              onClick={() => onSetStrategy('browser')}
            >
              Browser
            </Button>
            <Button
              size="sm"
              variant={strategy === 'dymo' ? 'default' : 'outline'}
              onClick={() => onSetStrategy('dymo')}
            >
              DYMO
            </Button>
          </div>
        </div>

        {strategy === 'dymo' ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
              <span>DYMO device</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void onRefresh()
                }}
                disabled={loadingPrinters}
              >
                {loadingPrinters ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
              value={selectedPrinter?.id ?? ''}
              disabled={loadingPrinters || printers.length === 0}
              onChange={event => {
                const next = event.target.value
                if (next) onSelectPrinter(next)
              }}
            >
              {printers.map(printer => (
                <option key={printer.id} value={printer.id}>
                  {printer.name}
                  {printer.model ? ` · ${printer.model}` : ''}
                </option>
              ))}
              {printers.length === 0 && <option value="">No printers detected</option>}
            </select>
            {error && (
              <div className="space-y-1">
                <p className="text-xs text-destructive">{error.message}</p>
                {errorHelp && (
                  <Button asChild variant="link" size="sm" className="h-auto p-0 text-[11px]">
                    <a href={errorHelp.url} target="_blank" rel="noreferrer">
                      {errorHelp.label}
                    </a>
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>
              Browser mode keeps this page open. Use your operating system print dialog to finish
              printing.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="ghost" size="sm">
                <Link to="/calibration/labels.html" target="_blank">
                  Calibration sheet
                </Link>
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 text-sm">
          <p className="text-sm font-medium">Maintenance</p>
          <Button onClick={handleTestPrint} disabled={isTesting} size="sm">
            {isTesting ? 'Sending test…' : 'Test Print'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Test Print sends a sample Exhibit ID to verify connectivity.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Recent jobs</p>
            <span className="text-xs text-muted-foreground">Last {recentJobs.length} jobs</span>
          </div>
          {recentJobs.length === 0 ? (
            <p className="text-xs text-muted-foreground">No label jobs yet.</p>
          ) : (
            <ul className="space-y-2 text-xs">
              {recentJobs.map(job => {
                const statusLabel = formatStatusLabel(job.status)
                const statusVariant =
                  job.status === 'success'
                    ? 'outline'
                    : job.status === 'error'
                      ? 'destructive'
                      : 'secondary'
                const jobLabel = job.key ?? job.id.slice(-6)
                return (
                  <li
                    key={job.id}
                    className="rounded-md border border-border bg-card px-3 py-2 shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={cn('font-medium', !job.key && 'font-mono')}>{jobLabel}</span>
                      <Badge variant={statusVariant} className="text-[11px] uppercase">
                        {statusLabel}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span>Attempts: {job.attempts}</span>
                      {job.nextRetryAt && job.status === 'pending' && <span>Retry scheduled</span>}
                    </div>
                    {job.errorMessage && (
                      <div className="mt-1 space-y-1 text-[11px]">
                        <p className="text-destructive">{job.errorMessage}</p>
                        {job.errorHelpUrl && (
                          <Button
                            asChild
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-[11px]"
                          >
                            <a href={job.errorHelpUrl} target="_blank" rel="noreferrer">
                              {job.errorHelpLabel ?? 'Open DYMO service in a new tab'}
                            </a>
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Labels require canonical Exhibit IDs and checked-in exhibits. Use the Print Label action
            after each check-in to keep packets accurate.
          </p>
          <Button asChild variant="outline" size="sm" className="w-full justify-center">
            <Link to={showAware('/entries', { currentSlug })}>View entries overview</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
